import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useFolder } from "src/core/contexts/FolderContext";
import { useCompareCustomerGraphWithExternalDataQuery } from "src/core/gql/graphql";
import { useViewModification } from "src/core/hooks/folder/useViewModification";

type FolderLocationState = { fromReexamination?: boolean };

export const useCompareAfterReexamination = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const locationState = location.state as FolderLocationState | undefined;
  const fromReexamination = locationState?.fromReexamination === true;
  const { folderFile } = useFolder();
  const [shouldShowCompareCustomerModal, setShouldShowCompareCustomerModal] =
    useState<boolean>(fromReexamination);
  const [openCompareCustomerModal, setOpenCompareCustomerModal] = useState<boolean>(false);
  const [openViewModificationsModal, setOpenViewModificationsModal] = useState<boolean>(false);

  useEffect(() => {
    if (shouldShowCompareCustomerModal) {
      setOpenCompareCustomerModal(true);
    }
  }, [shouldShowCompareCustomerModal]);

  const customerFolderId = folderFile.folderFile?.currentCustomerFolder?.id;
  const { data: compareData, loading: compareLoading } =
    useCompareCustomerGraphWithExternalDataQuery({
      variables: { customerFolderId: customerFolderId ?? "" },
      skip: (!openCompareCustomerModal && !openViewModificationsModal) || !customerFolderId,
      fetchPolicy: "no-cache",
    });

  const returnToFolder = () => {
    setShouldShowCompareCustomerModal(false);
    setOpenCompareCustomerModal(false);
    setOpenViewModificationsModal(false);
    navigate(location.pathname, { replace: true, state: {} });
  };

  const { handleSubmit } = useViewModification(
    compareData?.compareCustomerGraphWithExternalData,
    returnToFolder
  );

  const handleFolderAccess = async () => {
    if (compareData?.compareCustomerGraphWithExternalData?.stakeholdersWithDiffs?.length !== 0) {
      await handleSubmit();
    } else {
      returnToFolder();
    }
  };

  const handleViewModifications = () => {
    setOpenCompareCustomerModal(false);
    setOpenViewModificationsModal(true);
  };

  return {
    setShouldShowCompareCustomerModal,
    openCompareCustomerModal,
    setOpenCompareCustomerModal,
    compareData,
    compareLoading,
    returnToFolder,
    handleFolderAccess,
    handleViewModifications,
    openViewModificationsModal,
    setOpenViewModificationsModal,
  };
};
